import { useEffect, useState } from 'react'
import { DEPLOYMENT_PATH, LIVE_DEPLOYMENT } from '@sh/shared'
import type { Deployment } from '@sh/shared'

/**
 * What kind of deployment served this page.
 *
 * Asked over the wire rather than compiled in, because the bundle is built
 * once and handed out by every deployment: `import.meta.env.DEV` is baked
 * `false` into the copy a dev server serves, and `true` under `pnpm dev` even
 * when the proxy points at a live server. Both answers are about how the
 * JavaScript was compiled, and the question is about who is answering it.
 *
 * Fetched once per tab and shared, exactly as the skin catalog is, and for the
 * same reason: this cannot change under a running game, so every screen that
 * asks gets the same answer without a second request. Lazy, so only the lobby
 * ever pays for it.
 */
let pending: Promise<Deployment> | null = null

export function loadDeployment(): Promise<Deployment> {
  pending ??= fetch(DEPLOYMENT_PATH)
    .then((res) => (res.ok ? res.json() : LIVE_DEPLOYMENT))
    .then(parse)
    .catch(() => LIVE_DEPLOYMENT)
  return pending
}

/**
 * `live` until the server says otherwise, and for good if it never does.
 *
 * Four separate failures land on the same safe answer - no route, a body that
 * is not JSON, a body missing the field, and a dead connection - because the
 * thing behind this flag must be absent on a deployment that would not honour
 * it, rather than present and refused.
 */
export function useDeployment(): Deployment {
  const [deployment, setDeployment] = useState<Deployment>(LIVE_DEPLOYMENT)

  useEffect(() => {
    let cancelled = false
    void loadDeployment().then((next) => {
      if (!cancelled) setDeployment(next)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return deployment
}

function parse(value: unknown): Deployment {
  const raw = value as Partial<Deployment> | null
  if (raw?.env !== 'dev') return LIVE_DEPLOYMENT
  return { env: 'dev', maxBots: typeof raw.maxBots === 'number' ? raw.maxBots : 0 }
}
